/**
 * UnitSwitcher
 * Header control that lets a multi-unit user switch the ACTIVE unit.
 *
 * Flow:
 *   1. GET  /api/v1/users/me/units          → the units the user has role bindings in
 *   2. (only rendered when the user has > 1 switchable unit)
 *   3. POST /api/v1/users/me/switch-unit     → a new access token scoped to the chosen unit
 *   4. persist the new accessToken + reload  → the whole app re-resolves permissions/menu
 *      against the new unit (api.ts reads `accessToken` from localStorage on every request).
 *
 * Self-contained: uses only localStorage + the two endpoints, so it does not couple to
 * the shared auth store.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { profileService, type MyUnit } from '../../services/profile.service';

const ACTIVE_UNIT_KEY = 'activeUnitId';

interface UnitOption {
  label: string;
  value: string;
  roles: string;
}

const UnitSwitcher = () => {
  const [units, setUnits] = useState<MyUnit[]>([]);
  const [activeUnitId, setActiveUnitId] = useState<string | null>(
    localStorage.getItem(ACTIVE_UNIT_KEY)
  );
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let mounted = true;
    profileService
      .getMyUnits()
      .then((list) => {
        if (!mounted) return;
        setUnits(list);
        // Default the active unit to the home unit on first load.
        if (!localStorage.getItem(ACTIVE_UNIT_KEY)) {
          const home = list.find((u) => u.isHomeUnit) ?? list.find((u) => u.unitId);
          if (home?.unitId) setActiveUnitId(home.unitId);
        }
      })
      .catch(() => {
        if (mounted) setUnits([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Only real (non-global) units are switchable targets.
  const options: UnitOption[] = useMemo(
    () =>
      units
        .filter((u) => !!u.unitId)
        .map((u) => ({
          label: u.unitName || 'Unit',
          value: u.unitId as string,
          roles: (u.roles ?? [])
            .map((r) => r.roleName || r.roleKey)
            .filter(Boolean)
            .join(', '),
        })),
    [units]
  );

  // Nothing to switch for single-unit (or no-unit) users → render nothing.
  if (options.length <= 1) return null;

  const onSwitch = async (unitId: string) => {
    if (!unitId || unitId === activeUnitId || switching) return;
    setSwitching(true);
    try {
      const res = await profileService.switchUnit(unitId);
      if (res?.accessToken) {
        localStorage.setItem('accessToken', res.accessToken);
        localStorage.setItem(ACTIVE_UNIT_KEY, unitId);
        // Re-bootstrap so permissions, menu and every API call use the new unit-scoped token.
        window.location.reload();
      } else {
        setSwitching(false);
      }
    } catch (err) {
      console.error('Unit switch failed:', err);
      setSwitching(false);
    }
  };

  const itemTemplate = (opt: UnitOption) => (
    <div className="flex flex-col py-0.5">
      <span className="font-medium text-sm">{opt.label}</span>
      {opt.roles ? (
        <span className="text-xs text-ink-muted">{opt.roles}</span>
      ) : null}
    </div>
  );

  return (
    <div className="flex items-center gap-2" data-testid="Header.UnitSwitcher" title="Active unit">
      <i className="pi pi-building text-white" style={{ fontSize: '1.1rem' }} />
      <Dropdown
        value={activeUnitId}
        options={options}
        optionLabel="label"
        optionValue="value"
        onChange={(e) => onSwitch(e.value)}
        itemTemplate={itemTemplate}
        disabled={switching}
        loading={switching}
        placeholder="Select unit"
        className="w-52 text-sm"
        panelClassName="text-sm"
        aria-label="Switch active unit"
      />
    </div>
  );
};

export default UnitSwitcher;
